import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  DestroyRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { MutationObserverWrapper } from '../../services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type TextAlignment = 'left' | 'justify';
const NO_LINE_CLAMP = 'none';

/** This component extends native functionality of an element.
 * Sets height of the element based on linesCount specified.
 * Adds ellipsis if inner text will overflow the element.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[multilineTextWithElipsis]',
  templateUrl: './multiline-text-with-elipsis.component.html',
  styleUrls: ['./multiline-text-with-elipsis.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'multilineText'
})
export class MultilineTextWithElipsisComponent implements OnDestroy, OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private detacher = new Subject<void>();

  private changeDetectionSubject = new Subject();

  @ViewChild('container', { static: true })
  private containerElementRef: ElementRef;

  @Input()
  linesCount: number;

  @Input()
  textAlignment: TextAlignment = 'justify';

  @Input()
  multilineTextWithElipsis: boolean;

  @Input()
  calcContainerHeight = true;

  @Output()
  overflowChange = new EventEmitter<boolean>(true);

  wrapperHeight: number;
  isOverflown: boolean;

  constructor(private cd: ChangeDetectorRef, private mutationObserverWrapper: MutationObserverWrapper) {}

  ngOnDestroy(): void {
    this.detacher.next();
    this.detacher.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('linesCount' in changes) {
      this.changeDetectionSubject.next(undefined);
    }

    if ('multilineTextWithElipsis' in changes) {
      if (this.multilineTextWithElipsis) {
        this.setup();
      } else {
        this.detacher.next();
      }
    }
  }

  get isBlockWithTextOverflown(): boolean {
    return this.containerElementRef.nativeElement.scrollHeight > this.containerElementRef.nativeElement.offsetHeight;
  }

  get isJustifyAlignment(): boolean {
    return this.textAlignment === 'justify';
  }

  get isLeftAlignment(): boolean {
    return this.textAlignment === 'left';
  }

  get lineClamp(): number | string {
    return this.linesCount && this.isLeftAlignment ? this.linesCount : NO_LINE_CLAMP;
  }

  private setup(): void {
    this.detacher.next();

    this.changeDetectionSubject.pipe(debounceTime(0), filter(() => this.multilineTextWithElipsis), takeUntil(this.detacher), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.calculateProps();
      this.cd.detectChanges();
    });

    this.mutationObserverWrapper
      .observeAllChanges(this.containerElementRef.nativeElement)
      .pipe(takeUntil(this.detacher), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.changeDetectionSubject.next(undefined));

    this.changeDetectionSubject.next(undefined);
  }

  @HostListener('window:resize')
  @HostListener('mouseover')
  @HostListener('mouseleave')
  private runCalculation(): void {
    this.changeDetectionSubject.next(undefined);
  }

  private calculateProps(): void {
    this.calculateContainerHeight();
    this.syncOverflowingState();
  }

  private calculateContainerHeight(): void {
    const containerComputedStyles = window.getComputedStyle(this.containerElementRef.nativeElement);
    const lineHeightInPx = this.getNumberWithoutUnits(containerComputedStyles.lineHeight);
    this.wrapperHeight = lineHeightInPx * this.linesCount;
    this.cd.detectChanges();
  }

  private syncOverflowingState(): void {
    const previousState = this.isOverflown;
    this.isOverflown =
      this.containerElementRef.nativeElement.scrollHeight > this.containerElementRef.nativeElement.offsetHeight;

    if (previousState !== this.isOverflown) {
      this.overflowChange.emit(this.isOverflown);
    }
  }

  private getNumberWithoutUnits(inpt: string): number {
    return parseFloat(inpt.substring(0, inpt.length - 2));
  }
}
